import { Assignment, IAssignment } from '@app/models/Assignment'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export enum AssignmentsScope {
  All = 'All',
  Created = 'Created',
  Mine = 'Mine',
}

export interface IAssignmentsListState {
  readonly assignedTo: string
  readonly dueDate: string
  readonly error?: string
  readonly filterText: string
  readonly isLoading: boolean
  readonly items: List<IAssignment>
  readonly orderBy: string
  readonly scope: AssignmentsScope
  readonly sortOrder: string
  readonly status: string
  readonly totalItems: number
}

const AssignmentsListStateRecord = Record({
  assignedTo: '',
  dueDate: 'All',
  error: undefined,
  filterText: '',
  isLoading: false,
  items: List<Assignment>(),
  orderBy: 'Ascending',
  scope: AssignmentsScope.Mine,
  sortOrder: 'LastName',
  status: 'Incomplete',
  totalItems: 0,
})

export class AssignmentsListState
  extends AssignmentsListStateRecord
  implements IAssignmentsListState
{
  public static fromJS(initialState?: Partial<IAssignmentsListState>) {
    if (!initialState) {
      return new AssignmentsListState()
    }

    const { items, ...rest } = initialState
    return new AssignmentsListState({
      items: items ? List(items.map((i) => Assignment.fromJS(i))) : undefined,
      ...rest,
    })
  }

  public readonly assignedTo: string
  public readonly dueDate: string
  public readonly error?: string
  public readonly filterText: string
  public readonly isLoading: boolean
  public readonly items: List<Assignment>
  public readonly orderBy: string
  public readonly scope: AssignmentsScope
  public readonly sortOrder: string
  public readonly status: string
  public readonly totalItems: number

  constructor(props?: Partial<AssignmentsListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum AssignmentsListActionTypes {
  LoadAssignments = 'ASSIGNMENTS_LIST::LOAD_ASSIGNMENTS',
  LoadAssignmentsFailure = 'ASSIGNMENTS_LIST::LOAD_ASSIGNMENTS_FAILURE',
  LoadAssignmentsRequest = 'ASSIGNMENTS_LIST::LOAD_ASSIGNMENTS_REQUEST',
  LoadAssignmentsSuccess = 'ASSIGNMENTS_LIST::LOAD_ASSIGNMENTS_SUCCESS',
  SetAssignedTo = 'ASSIGNMENTS_LIST::SET_ASSIGNED_TO',
  SetAssignmentCompletion = 'ASSIGNMENTS_LIST::SET_ASSIGNMENT_COMPLETION',
  SetAssignmentPriority = 'ASSIGNMENTS_LIST::SET_ASSIGNMENT_PRIORITY',
  SetDueDate = 'ASSIGNMENTS_LIST::SET_DUE_DATE',
  SetFilterText = 'ASSIGNMENTS_LIST::SET_FILTER_TEXT',
  SetCompletionSuccess = 'ASSIGNMENTS_LIST::SET_COMPLETION_SUCCESS',
  SetOrderBy = 'ASSIGNMENTS_LIST::SET_ORDER_BY',
  SetPrioritySuccess = 'ASSIGNMENTS_LIST::SET_PRIORITY_SUCCESS',
  SetScope = 'ASSIGNMENTS_LIST::SET_SCOPE',
  SetStatus = 'ASSIGNMENTS_LIST::SET_STATUS',
  SetSortOrder = 'ASSIGNMENTS_LIST::SET_SORT_ORDER',
}

interface ILoadAssignmentsAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: AssignmentsListActionTypes.LoadAssignments
}

interface ILoadAssignmentsFailureAction {
  error: string
  type: AssignmentsListActionTypes.LoadAssignmentsFailure
}

interface ILoadAssignmentsRequestAction {
  type: AssignmentsListActionTypes.LoadAssignmentsRequest
}

interface ILoadAssignmentsSuccessAction {
  append: boolean
  items: Assignment[]
  totalItems: number
  type: AssignmentsListActionTypes.LoadAssignmentsSuccess
}

interface ILoadSetAssignedToAction {
  filter: string
  type: AssignmentsListActionTypes.SetAssignedTo
}

interface ISetAssignmentCompletionAction {
  id: string
  type: AssignmentsListActionTypes.SetAssignmentCompletion
  value: boolean
}

interface ISetAssignmentPriority {
  id: string
  type: AssignmentsListActionTypes.SetAssignmentPriority
  value: string
}

interface ISetDueDateAction {
  dueDate: string
  type: AssignmentsListActionTypes.SetDueDate
}

interface ISetFilterTextAction {
  filter: string
  type: AssignmentsListActionTypes.SetFilterText
}

interface ISetCompletionSuccessAction {
  id: string
  type: AssignmentsListActionTypes.SetCompletionSuccess
  value: boolean
}

interface ISetOrderByAction {
  orderBy: string
  type: AssignmentsListActionTypes.SetOrderBy
}

interface ISetPrioritySuccessAction {
  id: string
  type: AssignmentsListActionTypes.SetPrioritySuccess
  value: string
}

interface ISetScopeAction {
  scope: AssignmentsScope
  type: AssignmentsListActionTypes.SetScope
}

interface ISetSortOrderAction {
  sortOrder: string
  type: AssignmentsListActionTypes.SetSortOrder
}

interface ISetStatusAction {
  status: string
  type: AssignmentsListActionTypes.SetStatus
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadAssignmentsFailureAction
  | ILoadAssignmentsRequestAction
  | ILoadAssignmentsSuccessAction
  | ILoadSetAssignedToAction
  | ISetDueDateAction
  | ISetFilterTextAction
  | ISetCompletionSuccessAction
  | ISetOrderByAction
  | ISetPrioritySuccessAction
  | ISetScopeAction
  | ISetSortOrderAction
  | ISetStatusAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadAssignments(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadAssignmentsAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: AssignmentsListActionTypes.LoadAssignments,
  }
}

function loadAssignmentsFailure(error: string): ILoadAssignmentsFailureAction {
  return {
    error,
    type: AssignmentsListActionTypes.LoadAssignmentsFailure,
  }
}

function loadAssignmentsRequest(): ILoadAssignmentsRequestAction {
  return {
    type: AssignmentsListActionTypes.LoadAssignmentsRequest,
  }
}

function loadAssignmentsSuccess(
  items: Assignment[],
  append: boolean,
  totalItems: number,
): ILoadAssignmentsSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: AssignmentsListActionTypes.LoadAssignmentsSuccess,
  }
}

function setAssignedTo(filter: string): ILoadSetAssignedToAction {
  return {
    filter,
    type: AssignmentsListActionTypes.SetAssignedTo,
  }
}

function setAssignmentCompletion(
  id: string,
  value: boolean,
): ISetAssignmentCompletionAction {
  return {
    id,
    type: AssignmentsListActionTypes.SetAssignmentCompletion,
    value,
  }
}

function setAssignmentPriority(
  id: string,
  value: string,
): ISetAssignmentPriority {
  return {
    id,
    type: AssignmentsListActionTypes.SetAssignmentPriority,
    value,
  }
}

function setDueDate(dueDate: string): ISetDueDateAction {
  return {
    dueDate,
    type: AssignmentsListActionTypes.SetDueDate,
  }
}

function setFilterText(filter: string): ISetFilterTextAction {
  return {
    filter,
    type: AssignmentsListActionTypes.SetFilterText,
  }
}

function setCompletionSuccess(
  id: string,
  value: boolean,
): ISetCompletionSuccessAction {
  return {
    id,
    type: AssignmentsListActionTypes.SetCompletionSuccess,
    value,
  }
}

function setOrderBy(orderBy: string) {
  return {
    orderBy,
    type: AssignmentsListActionTypes.SetOrderBy,
  }
}

function setPrioritySuccess(
  id: string,
  value: string,
): ISetPrioritySuccessAction {
  return {
    id,
    type: AssignmentsListActionTypes.SetPrioritySuccess,
    value,
  }
}

function setScope(scope: AssignmentsScope): ISetScopeAction {
  return {
    scope,
    type: AssignmentsListActionTypes.SetScope,
  }
}

function setSortOrder(sortOrder: string) {
  return {
    sortOrder,
    type: AssignmentsListActionTypes.SetSortOrder,
  }
}

function setStatus(status: string): ISetStatusAction {
  return {
    status,
    type: AssignmentsListActionTypes.SetStatus,
  }
}

export const actionCreators = {
  loadAssignments,
  setAssignedTo,
  setAssignmentCompletion,
  setAssignmentPriority,
  setDueDate,
  setFilterText,
  setOrderBy,
  setScope,
  setSortOrder,
  setStatus,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<AssignmentsListState> = (
  state: AssignmentsListState = new AssignmentsListState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case AssignmentsListActionTypes.LoadAssignmentsFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as AssignmentsListState

    case AssignmentsListActionTypes.LoadAssignmentsRequest:
      return state.set('isLoading', true) as AssignmentsListState

    case AssignmentsListActionTypes.LoadAssignmentsSuccess:
      const newItems = action.append
        ? (state.items.concat(List(action.items)) as List<Assignment>)
        : List(action.items)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set('totalItems', action.totalItems) as AssignmentsListState

    case AssignmentsListActionTypes.SetAssignedTo:
      return state.set('assignedTo', action.filter) as AssignmentsListState

    case AssignmentsListActionTypes.SetDueDate:
      return state.set('dueDate', action.dueDate) as AssignmentsListState

    case AssignmentsListActionTypes.SetFilterText:
      return state.set('filterText', action.filter) as AssignmentsListState

    case AssignmentsListActionTypes.SetCompletionSuccess:
      const completionIndex = state.items.findIndex((item: any) => {
        return item.id === action.id
      })

      if (completionIndex > -1) {
        return state.setIn(
          ['items', completionIndex, 'isComplete'],
          action.value,
        ) as AssignmentsListState
      }

      return state as AssignmentsListState

    case AssignmentsListActionTypes.SetOrderBy:
      return state.set('orderBy', action.orderBy) as AssignmentsListState

    case AssignmentsListActionTypes.SetPrioritySuccess:
      const assignmentIndex = state.items.findIndex((item: any) => {
        return item.id === action.id
      })

      if (assignmentIndex > -1) {
        return state.setIn(
          ['items', assignmentIndex, 'priority'],
          action.value,
        ) as AssignmentsListState
      }

      return state as AssignmentsListState

    case AssignmentsListActionTypes.SetScope:
      return state.set('scope', action.scope) as AssignmentsListState

    case AssignmentsListActionTypes.SetSortOrder:
      return state.set('sortOrder', action.sortOrder) as AssignmentsListState

    case AssignmentsListActionTypes.SetStatus:
      return state.set('status', action.status) as AssignmentsListState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadAssignmentsSaga(action: ILoadAssignmentsAction) {
  try {
    yield put(loadAssignmentsRequest())

    const currentState: AssignmentsListState = yield select(
      (state: ApplicationState) => state.assignmentsList,
    )

    const query: string[] = []

    if (currentState.assignedTo) {
      query.push(`assignedTo=${currentState.assignedTo}`)
    }

    if (currentState.dueDate && currentState.dueDate !== 'All') {
      query.push(`date=${currentState.dueDate}`)
    }

    if (currentState.filterText) {
      query.push(`filter=${encodeURIComponent(currentState.filterText)}`)
    }

    if (currentState.orderBy) {
      query.push(`orderBy=${currentState.orderBy}`)
    }

    if (currentState.sortOrder !== 'LastName') {
      query.push(`sort=${currentState.sortOrder}`)
    }

    if (currentState.status) {
      query.push(`status=${currentState.status}`)
    }

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    let baseUrl = '/api/Assignments/My/All'

    if (currentState.scope === AssignmentsScope.Created) {
      baseUrl = '/api/Assignments/Created'
    } else if (currentState.scope === AssignmentsScope.Mine) {
      baseUrl = '/api/Assignments/My'
    }

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const contacts: Assignment[] = result.results.map((c: any) =>
        Assignment.fromJS(c),
      )
      yield put(
        loadAssignmentsSuccess(contacts, action.append, result.paging.total),
      )
    } else {
      yield put(loadAssignmentsFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadAssignmentsFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* setCompletionSaga(action: ISetAssignmentCompletionAction) {
  try {
    const url = `/api/Assignments/${action.id}`

    const currentState: AssignmentsListState = yield select(
      (state: ApplicationState) => state.assignmentsList,
    )

    const assignment = currentState.items
      .find((a: any) => a.id === action.id)
      .toJS()

    if (assignment) {
      assignment.isComplete = action.value
      const opts = {
        body: JSON.stringify(assignment),
        method: RequestMethod.PUT,
      }

      const result = yield makeAuthenticatedRequest(url, opts)

      if (result.success === true) {
        yield put(setCompletionSuccess(action.id, action.value))
      } else {
        yield put(errorToast('Oops!', result.message))
      }
    } else {
      yield put(errorToast('Oops!', 'Could not find the assignment'))
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* setPrioritySaga(action: ISetAssignmentPriority) {
  try {
    const url = `/api/Assignments/${action.id}`

    const currentState: AssignmentsListState = yield select(
      (state: ApplicationState) => state.assignmentsList,
    )

    const assignment = currentState.items
      .find((a: any) => a.id === action.id)
      .toJS()

    if (assignment) {
      assignment.priority = action.value
      assignment.isComplete = false
      const opts = {
        body: JSON.stringify(assignment),
        method: RequestMethod.PUT,
      }

      const result = yield makeAuthenticatedRequest(url, opts)

      if (result.success === true) {
        yield put(setPrioritySuccess(action.id, action.value))
      } else {
        yield put(errorToast('Oops!', result.message))
      }
    } else {
      yield put(errorToast('Oops!', 'Could not find the assignment'))
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadAssignments() {
  yield takeLatest(
    AssignmentsListActionTypes.LoadAssignments,
    loadAssignmentsSaga,
  )
}

function* watchSetAssignmentCompletion() {
  yield takeLatest(
    AssignmentsListActionTypes.SetAssignmentCompletion,
    setCompletionSaga,
  )
}

function* watchSetAssignmentPriority() {
  yield takeLatest(
    AssignmentsListActionTypes.SetAssignmentPriority,
    setPrioritySaga,
  )
}

export function* assignmentsListRootSaga() {
  yield fork(watchLoadAssignments)
  yield fork(watchSetAssignmentCompletion)
  yield fork(watchSetAssignmentPriority)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState =
  () =>
  (state: ApplicationState): AssignmentsListState =>
    state.assignmentsList
export const selectAssignedTo = () =>
  createSelector(selectModuleState(), (mState) => mState.assignedTo)

export const selectDueDate = () =>
  createSelector(selectModuleState(), (mState) => mState.dueDate)

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectFilterText = () =>
  createSelector(selectModuleState(), (mState) => mState.filterText)

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectItems = () =>
  createSelector(selectModuleState(), (mState) => mState.items)

export const selectOrderBy = () =>
  createSelector(selectModuleState(), (mstate) => mstate.orderBy)

export const selectScope = () =>
  createSelector(selectModuleState(), (mState) => mState.scope)

export const selectSortOrder = () =>
  createSelector(selectModuleState(), (mState) => mState.sortOrder)

export const selectStatus = () =>
  createSelector(selectModuleState(), (mState) => mState.status)

export const selectTotalItems = () =>
  createSelector(selectModuleState(), (mState) => mState.totalItems)
